import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Baby, KeyRound, LogIn, ShieldAlert, UsersRound } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth, isRespAdmin } from "@/context/AuthContext";
import { AppLogo } from "@/components/Shared";
import InformativaBox from "@/components/InformativaBox";

/**
 * Accesso volutamente organizzato come una scelta di percorso: chi arriva per
 * la prima volta non deve distinguere tre link quasi identici sotto al form.
 * La logica di autenticazione resta invariata; cambia solo la presentazione.
 */
export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("choice");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [telefono, setTelefono] = useState("");
  const [codice, setCodice] = useState("");
  const [presaVisione, setPresaVisione] = useState(false);
  const [error, setError] = useState("");
  const [avviso, setAvviso] = useState("");
  const [loading, setLoading] = useState(false);
  const [attesa, setAttesa] = useState(0);
  const [activationStep, setActivationStep] = useState(1);
  const [activationPreview, setActivationPreview] = useState(null);
  const [dataNascita, setDataNascita] = useState("");
  const [sesso, setSesso] = useState("");
  const [tagliaMaglia, setTagliaMaglia] = useState("");
  const [intolleranze, setIntolleranze] = useState("");

  const isPasswordReset = activationPreview?.tipo_operazione === "reimpostazione_password";
  const passwordMinimum = activationPreview?.ruolo === "responsabile" ? 12 : 8;
  const activationStepCount = isPasswordReset ? 2 : 4;

  useEffect(() => {
    if (attesa <= 0) return undefined;
    const timer = setInterval(() => setAttesa((seconds) => (seconds <= 1 ? 0 : seconds - 1)), 1000);
    return () => clearInterval(timer);
  }, [attesa > 0]);

  useEffect(() => {
    try {
      const msg = sessionStorage.getItem("avviso_accesso_staff");
      if (msg) {
        setAvviso(msg);
        sessionStorage.removeItem("avviso_accesso_staff");
      }
    } catch { /* sessionStorage può essere disabilitato */ }
  }, []);

  const attesaLeggibile = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return minutes > 0 ? `${minutes}:${String(remainder).padStart(2, "0")} min` : `${remainder} s`;
  };

  const enter = (user) => {
    setUser(user);
    navigate(user?.role === "genitore" ? "/genitore" : (isRespAdmin(user) ? "/dashboard" : "/inizio"));
  };

  const chooseStep = (nextStep) => {
    setError("");
    if (nextStep === "activation") {
      setActivationStep(1);
      setActivationPreview(null);
      setUsername("");
      setNewPw("");
      setConfirmPw("");
      setTelefono("");
      setDataNascita("");
      setSesso("");
      setTagliaMaglia("");
      setIntolleranze("");
      setPresaVisione(false);
    }
    setStep(nextStep);
  };

  const backToChoices = () => {
    setError("");
    setStep("choice");
  };

  const doLogin = async (event) => {
    event.preventDefault();
    if (attesa > 0) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      enter(data);
    } catch (err) {
      if (err.response?.status === 429) setAttesa(err.retryAfter || 60);
      setError(errMsg(err, "Credenziali non valide"));
    } finally {
      setLoading(false);
    }
  };

  const doAttiva = async (event) => {
    event?.preventDefault?.();
    setError("");
    if (attesa > 0) return;
    if (newPw.length < passwordMinimum) return setError(`La password deve avere almeno ${passwordMinimum} caratteri`);
    if (newPw !== confirmPw) return setError("Le password non coincidono");
    if (!isPasswordReset) {
      if (telefono.replace(/\D/g, "").length < 8) return setError("Inserisci un numero di cellulare valido");
      if (!dataNascita) return setError("Indica la data di nascita");
      if (!sesso) return setError("Indica il sesso");
      if (!tagliaMaglia) return setError("Indica la taglia della maglia");
      if (!presaVisione) return setError("Devi dichiarare di aver letto l'informativa sul trattamento dei dati");
    }
    setLoading(true);
    try {
      const payload = isPasswordReset
        ? { username, codice, nuova_password: newPw }
        : {
            username, codice, nuova_password: newPw, cellulare: telefono,
            data_nascita: dataNascita, sesso, taglia_maglia: tagliaMaglia, intolleranze,
            informativa_presa_visione: presaVisione,
          };
      const { data } = await api.post("/auth/attiva", payload);
      enter(data);
    } catch (err) {
      if (err.response?.status === 429) setAttesa(err.retryAfter || 900);
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const doPreview = async (event) => {
    event.preventDefault();
    setError("");
    if (attesa > 0) return;
    if (!codice.trim()) return setError("Inserisci il codice di attivazione");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/attivazione/anteprima", { codice });
      setActivationPreview(data);
      setUsername(data.username || "");
      setActivationStep(2);
    } catch (err) {
      if (err.response?.status === 429) setAttesa(err.retryAfter || 900);
      setError(errMsg(err, "Codice di attivazione non valido"));
    } finally {
      setLoading(false);
    }
  };

  const nextActivationStep = () => {
    setError("");
    if (activationStep === 2) {
      if (isPasswordReset) return;
      if (newPw.length < passwordMinimum) return setError(`La password deve avere almeno ${passwordMinimum} caratteri`);
      if (newPw !== confirmPw) return setError("Le password non coincidono");
    }
    if (activationStep === 3) {
      if (telefono.replace(/\D/g, "").length < 8) return setError("Inserisci un numero di cellulare valido");
      if (!dataNascita) return setError("Indica la data di nascita");
      if (!sesso) return setError("Indica il sesso");
      if (!tagliaMaglia) return setError("Indica la taglia della maglia");
    }
    setActivationStep((current) => Math.min(4, current + 1));
  };

  const activationStepHeader = (title, description) => (
    <div className="login-step-header">
      <button
        type="button"
        onClick={() => activationStep === 1 ? backToChoices() : setActivationStep((current) => current - 1)}
        className="login-back-button"
        data-testid="attivazione-indietro-btn"
      >
        <ArrowLeft className="h-4 w-4" /> {activationStep === 1 ? "Cambia percorso" : "Indietro"}
      </button>
      <p className="login-eyebrow">{isPasswordReset ? "Recupero accesso" : "Attivazione account"} · {activationStep}/{activationStepCount}</p>
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
  );

  const stepHeader = (eyebrow, title, description) => (
    <div className="login-step-header">
      <button
        type="button"
        onClick={backToChoices}
        className="login-back-button"
        data-testid="torna-login-btn"
      >
        <ArrowLeft className="h-4 w-4" /> Cambia percorso
      </button>
      <p className="login-eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
  );

  const accessChoices = (
    <div data-testid="login-choice" className="login-flow">
      <div className="login-section-heading">
        <p className="login-eyebrow">Benvenuto</p>
        <h2>Accedi o crea il tuo profilo</h2>
        <p>Scegli il percorso adatto a te.</p>
      </div>

      <AccessChoiceCard
        icon={LogIn}
        title="Accedi"
        description="Ho già un account"
        tone="primary"
        onClick={() => chooseStep("login")}
        testId="scegli-login-btn"
      />

      <button
        type="button"
        onClick={() => chooseStep("register")}
        className="login-route-row login-route-secondary"
        data-testid="scegli-registrazione-btn"
      >
        <span className="login-route-icon login-route-icon-amber"><UsersRound className="h-5 w-5" /></span>
        <span className="login-route-copy"><span className="login-route-title">Registrati</span><span className="login-route-description">Genitore/tutore o animatore</span></span>
        <span className="login-route-arrow" aria-hidden="true">›</span>
      </button>

      <div className="login-divider" aria-hidden="true"><span>oppure</span></div>

      <button type="button" onClick={() => chooseStep("activation")} className="login-code-link" data-testid="vai-attivazione-btn">
        <KeyRound className="h-4 w-4" /> Hai ricevuto un codice? Attiva o recupera l'account
      </button>
    </div>
  );

  const registrationChoices = (
    <div data-testid="login-registration-choice" className="login-flow">
      <button type="button" onClick={backToChoices} className="login-back-button">
        <ArrowLeft className="h-4 w-4" /> Torna indietro
      </button>
      <div className="login-section-heading">
        <p className="login-eyebrow">Registrati</p>
        <h2>Crea il tuo profilo</h2>
        <p>Scegli il percorso adatto a te.</p>
      </div>
      <AccessChoiceCard icon={Baby} title="Genitore o tutore" description="Crea il profilo e aggiungi i minori" tone="emerald" href="/genitore/registrazione" testId="vai-iscrizione-genitore-link" />
      <AccessChoiceCard icon={UsersRound} title="Animatore" description="Invia una richiesta di accesso" tone="sky" href="/registrazione" testId="vai-registrazione-link" />
    </div>
  );

  const loginForm = (
    <>
      {stepHeader("Accesso personale", "Accedi al tuo account", "Usa il nome utente, l'email o il cellulare associato al profilo.")}
      <form onSubmit={doLogin} data-testid="login-form">
        <label className="login-label">Nome utente, email o cellulare</label>
        <input className="login-input" type="text" required value={username} onChange={(event) => setUsername(event.target.value)}
          placeholder="mario.rossi oppure 333 1234567" autoCapitalize="none" autoCorrect="off"
          data-testid="login-email-input" />
        <label className="login-label">Password</label>
        <input className="login-input" type="password" required value={password} onChange={(event) => setPassword(event.target.value)} placeholder="••••••••" data-testid="login-password-input" />
        {error && <p className="login-error" role="alert" data-testid="login-error">{error}</p>}
        {attesa > 0 && <p className="login-wait" data-testid="login-attesa">Nuovo tentativo disponibile tra {attesaLeggibile(attesa)}</p>}
        <button className="login-submit" disabled={loading || attesa > 0} data-testid="login-submit-button">
          {attesa > 0 ? `Attendi ${attesaLeggibile(attesa)}` : loading ? "Accesso..." : "Entra"}
        </button>
        <button type="button" onClick={() => chooseStep("activation")}
          className="login-code-link" data-testid="login-attivazione-link">
          <KeyRound className="h-4 w-4" /> Hai un codice? Attiva o recupera l'account
        </button>
      </form>
    </>
  );

  const activationForm = (
    <>
      {activationStep === 1 && <>
        {activationStepHeader("Inserisci il codice", "Usa il codice personale consegnato dall'Amministratore.")}
        <form onSubmit={doPreview} data-testid="attiva-step1-form">
          <div className="login-info login-info-sky">
            <KeyRound className="h-5 w-5" />
            <p>Non è il codice della stagione usato per registrarsi: è il codice personale del tuo account.</p>
          </div>
          <label className="label-app">Codice di attivazione</label>
          <input className="input-app mb-4 uppercase tracking-widest font-mono" autoFocus required value={codice}
            onChange={(event) => setCodice(event.target.value.toUpperCase())} placeholder="XXXX-XXXX"
            autoCapitalize="characters" autoCorrect="off" spellCheck="false" data-testid="attiva-codice-input" />
          {error && <p className="badge-neg block w-full text-center py-2 mb-4 !text-sm" data-testid="attiva-error">{error}</p>}
          {attesa > 0 && <p className="text-xs font-bold text-red-600 text-center mb-3" data-testid="attiva-attesa">Troppi tentativi. Riprova tra {attesaLeggibile(attesa)}</p>}
          <button className="btn-primary w-full !py-3.5 text-base" disabled={loading || attesa > 0} data-testid="attiva-step1-submit">
            {attesa > 0 ? `Attendi ${attesaLeggibile(attesa)}` : loading ? "Verifica..." : "Avanti"}
          </button>
        </form>
      </>}

      {activationStep === 2 && <>
        {activationStepHeader(
          `Ciao ${activationPreview?.nome || ""} ${activationPreview?.cognome || ""}`,
          isPasswordReset ? "Scegli una nuova password per rientrare nel tuo account." : "Scegli la password per il tuo account."
        )}
        <div className="login-info login-info-emerald">
          <p className="login-info-title">Nome utente associato al codice</p>
          <p className="login-info-value">{username}</p>
          <p>Non può essere modificato.</p>
        </div>
        <label className="label-app">{isPasswordReset ? "Scegli la nuova password" : "Scegli la password"} <span className="text-slate-400 font-normal normal-case">(almeno {passwordMinimum} caratteri)</span></label>
        <input className="input-app mb-4" type="password" required value={newPw} onChange={(event) => setNewPw(event.target.value)} data-testid="attiva-password-input" />
        <label className="label-app">Conferma password</label>
        <input className="input-app mb-4" type="password" required value={confirmPw} onChange={(event) => setConfirmPw(event.target.value)} data-testid="attiva-confirm-input" />
        {error && <p className="badge-neg block w-full text-center py-2 mb-4 !text-sm" data-testid="attiva-error">{error}</p>}
        <button type="button" className="btn-primary w-full !py-3.5 text-base" onClick={isPasswordReset ? doAttiva : nextActivationStep} disabled={loading || attesa > 0}>
          {isPasswordReset ? (loading ? "Reimpostazione..." : "Reimposta password") : "Avanti"}
        </button>
      </>}

      {activationStep === 3 && <>
        {activationStepHeader("I tuoi dati personali", "Completa i dati necessari per il tuo profilo.")}
        <label className="label-app">Numero di telefono</label>
        <input className="input-app mb-4" type="tel" required value={telefono} onChange={(event) => setTelefono(event.target.value)} placeholder="Es. 333 1234567" data-testid="attiva-telefono-input" />
        <label className="label-app">Data di nascita</label>
        <input className="input-app mb-4" type="date" required value={dataNascita} onChange={(event) => setDataNascita(event.target.value)} data-testid="attiva-data-nascita-input" />
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="label-app">Sesso</label>
            <select className="input-app" required value={sesso} onChange={(event) => setSesso(event.target.value)} data-testid="attiva-sesso-input">
              <option value="">Seleziona</option><option value="M">Maschile</option><option value="F">Femminile</option>
            </select>
          </div>
          <div>
            <label className="label-app">Taglia maglia</label>
            <select className="input-app" required value={tagliaMaglia} onChange={(event) => setTagliaMaglia(event.target.value)} data-testid="attiva-taglia-input">
              <option value="">Seleziona</option>{["S", "M", "L", "XL", "XXL"].map((size) => <option key={size} value={size}>{size}</option>)}
            </select>
          </div>
        </div>
        {error && <p className="badge-neg block w-full text-center py-2 mb-4 !text-sm" data-testid="attiva-error">{error}</p>}
        <button type="button" className="btn-primary w-full !py-3.5 text-base" onClick={nextActivationStep}>Avanti</button>
      </>}

      {activationStep === 4 && <>
        {activationStepHeader("Ultimo passaggio", "Allergie, intolleranze e privacy.")}
        <label className="label-app">Intolleranze e allergie <span className="text-slate-400 font-normal normal-case">(facoltativo)</span></label>
        <textarea className="input-app min-h-28 mb-4" value={intolleranze} onChange={(event) => setIntolleranze(event.target.value)} placeholder="Scrivi qui eventuali informazioni utili..." data-testid="attiva-intolleranze-input" />
        <InformativaBox tipo="staff" valore={presaVisione} etichetta="Dichiaro di aver letto l'informativa sul trattamento dei dati."
          onChange={({ presaVisione: value }) => setPresaVisione(value)} testId="informativa-primo-accesso" />
        {error && <p className="badge-neg block w-full text-center py-2 mb-4 !text-sm" data-testid="attiva-error">{error}</p>}
        {attesa > 0 && <p className="text-xs font-bold text-red-600 text-center mb-3" data-testid="attiva-attesa">Troppi tentativi. Riprova tra {attesaLeggibile(attesa)}</p>}
        <button type="button" className="btn-primary w-full !py-3.5 text-base" onClick={doAttiva} disabled={loading || attesa > 0 || !presaVisione} data-testid="attiva-submit-button">
          {attesa > 0 ? `Attendi ${attesaLeggibile(attesa)}` : loading ? "Attivazione..." : activationPreview?.ruolo === "responsabile" ? "Attiva account Responsabile" : "Attiva account"}
        </button>
      </>}
    </>
  );

  return (
    <main className="login-page">
      <div className="login-shell">
        <section className="login-card">
        <div className="login-brand">
          <AppLogo size={68} className="login-logo" />
          <h1>Oratorio SS. Medici</h1>
          <p>Bitonto</p>
        </div>

        {avviso && (
          <div className="login-alert" role="alert" data-testid="avviso-accesso-bloccato">
            <ShieldAlert className="h-5 w-5" />
            <p>{avviso}</p>
          </div>
        )}

        {step === "choice" ? accessChoices : step === "login" ? loginForm : step === "register" ? registrationChoices : activationForm}

        </section>
        <p className="login-privacy"><Link to="/privacy" data-testid="login-privacy-link">Informativa sulla privacy</Link></p>
      </div>
    </main>
  );
}

function AccessChoiceCard({ icon: Icon, title, description, tone, href, onClick, testId }) {
  const iconClass = tone === "emerald" ? "login-route-icon-emerald" : tone === "sky" ? "login-route-icon-sky" : "login-route-icon-amber";
  const className = `login-route-row ${tone === "primary" ? "login-route-primary" : "login-route-secondary"}`;
  const content = (
    <>
      <span className={`login-route-icon ${iconClass}`}><Icon className="h-5 w-5" /></span>
      <span className="login-route-copy">
        <span className="login-route-title">{title}</span>
        <span className="login-route-description">{description}</span>
      </span>
      <span className="login-route-arrow" aria-hidden="true">›</span>
    </>
  );
  if (href) return <Link to={href} className={className} data-testid={testId}>{content}</Link>;
  return <button type="button" onClick={onClick} className={className} data-testid={testId}>{content}</button>;
}
